import React, { useMemo, useState, forwardRef } from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik,
  FieldCustom,
  FieldImageGalleryUpload,
  FieldMarkdown,
  FieldSelect,
} from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormikForwardRef } from 'hooks';
import { useDebounce } from 'react-use';
import { linkRegex } from 'services/constants';
import { generateKey } from 'services/generators';
import SliderCell from './SliderCell';

const ItemSchema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  description: Yup.string().required('Campo obrigatório'),
  href: Yup.string().matches(
    linkRegex,
    'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.'
  ),
  image: Yup.object({
    url: Yup.string().required('Campo de imagem obrigatório'),
    alt: Yup.string(),
    title: Yup.string(),
  }).required('Campo de imagem obrigatório'),
});

const ItemWithLinkSchema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  href: Yup.string().matches(
    linkRegex,
    'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.'
  ),
  image: Yup.object({
    url: Yup.string().required('Campo de imagem obrigatório'),
    alt: Yup.string(),
    title: Yup.string(),
  }).required('Campo de imagem obrigatório'),
});

// eslint-disable-next-line react/prop-types
const CampusForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  const initialValuesComputed = useMemo(() => {
    const { 
      title, description, src, alt, hasLink, href
    } = initialValues;

    return {
      title,
      description,
      hasLink: hasLink === 'undefined' ? false : hasLink,
      href,
      image: {
        url: src,
        alt,
        title: '',
      },
    };
  }, [initialValues]);

  const isImagesMode = initialValues.hasLink ?? false

  const formToModuleSchema = values => {
    const {
      title,
      description,
      hasLink,
      href,
      image: { url, alt },
    } = values;
    return {
      title,
      description,
      hasLink,
      href,
      src: url,
      alt,
      hasTitle: true,
    };
  };

  return (
    <Formik
      ref={formRef}
      initialValues={initialValuesComputed}
      validationSchema={isImagesMode ? ItemWithLinkSchema : ItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(formToModuleSchema(values));
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      {({ values }) => (
        <Form>
          <Field
            title="Imagem"
            name="image"
            component={FieldImageGalleryUpload}
          />
          <Field
            title="Título"
            name="title"
            info="Título do destaque"
            component={FieldCustom}
          />

          {values.hasLink === false && (
            <>
              <Field
                title="Descrição"
                name="description"
                component={FieldMarkdown}
              />
            </>
          )}
          {values.hasLink === true && (
            <>
              <Field title="Link" name="href" component={FieldCustom} />
            </>
          )}
        </Form>
      )}
    </Formik>
  );
});

const Schema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  description: Yup.string(),
  columns: Yup.number().required('Campo obrigatório'),
});

const GalleryEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps);

  const initialValues = useMemo(() => {
    const {
      title,
      columns,
      description,
      images,
      imagesMode,
      ...values
    } = initialProps;

    images.map(item => {
      const hasLink = imagesMode !== 'noLink';
      return { item, hasLink };
    });

    return {
      title,
      columns,
      description,
      images,
      ...values,
      imagesMode,
    };
  }, [initialProps]);

  const handleFormChange = newValues => {
    const {
      title,
      url,
      description,
      images,
      imagesMode,
      ...values
    } = newValues;

    const newList = images.map(item => {
      const hasLink = imagesMode !== 'noLink';
      return { ...item, hasLink };
    });

    const newProps = {
      ...initialProps,
      ...values,
      title,
      url,
      description,
      imagesMode,
      images: newList,
    };

    const isValid = Schema.isValidSync(newProps);
    if (isValid) setEditorProps(newProps);
  };

  useDebounce(
    () => {
      onChange(editorProps);
    },
    200,
    [editorProps]
  );

  const columns = useMemo(
    () => [
      {
        accessor: 'src',
        Cell: SliderCell,
      },
      {
        accessor: 'title',
      },
    ],
    []
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Schema}
      validate={handleFormChange}
      enableReinitialize
    >
      <Form>
        <Field
          title="Título"
          name="title"
          info="Título da secção"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          title="Descrição"
          name="description"
          component={FieldMarkdown}
          maxLetters={400}
        />
        <Field
          title="Colunas"
          name="columns"
          component={FieldCustom}
          type="range"
          min="1"
          max="5"
          info={`${initialValues.columns} colunas por linha`}
        />
        <Field
          title="Modo"
          name="imagesMode"
          component={FieldSelect}
          placeholder="Selecione o modelo..."
          options={[
            { value: 'noLink', label: 'Modo Teatro' },
            { value: 'withLink', label: 'Com link' },
          ]}
        />
        <Field
          title="Imagens"
          name="images"
          newItemText="Adicionar imagem"
          component={FieldArrayDndFormik}
          extra={{ hasLink: initialProps.imagesMode === 'withLink' }}
          columns={columns}
          rowKey={item => generateKey(item.title)}
          ItemForm={CampusForm}
          shouldAddOnTop
          shouldHideHeaders
        />
      </Form>
    </Formik>
  );
};

GalleryEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    title: PropTypes.string,
    columns: PropTypes.string,
  }),
};

GalleryEditor.defaultProps = {
  initialProps: {},
};

export default GalleryEditor;
